import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  MenuItem,
  Typography,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  SubmitHandler,
  useForm,
  Controller,
  FormProvider,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { GeoEventRegion } from 'features/geoEvents/geoEventRegion.types';
import {
  MarkerEditLocationArg,
  useMarkerEditLock,
} from 'features/geoEvents/hooks/useMarkerEditLock';
import { useMarkerEditRegionSync } from 'features/geoEvents/hooks/useMarkerEditRegionSync';
import { useMarkerMoveConfirmation } from 'features/geoEvents/hooks/useMarkerMoveConfirmation';
import { useInitialMapZoom } from 'hooks/useInitialMapZoom';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import {
  Wildfire,
  WildfireCreateUpdateData,
} from 'features/wildfires/wildfire.types';
import { GeoEventFormTabs } from 'features/geoEvents/components/GeoEventFormTabs';
import { InternalFormSection } from 'features/geoEvents/components/InternalFormSection';
import { invalidateGeoEventQueries } from 'features/geoEvents/hooks/useGeoEvent';
import { getIncidentRemountKey } from 'features/geoEvents/utils/getIncidentRemountKey';
import {
  buildReporterNotesPayload,
  reporterNotesDefaultFormValues,
  reporterNotesValidationSchema,
} from 'features/reporterNotes/reporterNotes.utils';
import { API } from 'api';
import { enqueueSnackbar } from 'notistack';
import { logUnexpectedErrorAndNotifyUserWithTryAgain } from 'shared/handleUnexpectedError';
import { FormWithoutEnter } from 'components/FormWithoutEnter';
import { AxiosResponse, HttpStatusCode, isAxiosError } from 'axios';
import { InternalNotesInput } from 'components/InternalNotesInput';
import { MapFormLayout } from 'components/MapFormLayout';
import { AutocompleteWithBackdrop } from 'components/AutocompleteWithBackdrop';
import { Loader } from 'components/Loader';
import { InlineStatus } from 'components/InlineStatus';
import { useProvisionalGeoEventActions } from 'features/provisionalSubmissions/hooks/useProvisionalGeoEventActions';
import { EditGeoEventProvisionalSubmissionResponse } from 'features/provisionalSubmissions/provisionalSubmissions.types';
import { usePermissions } from 'hooks/usePermissions';
import { useUserRegionsQuery } from 'hooks/useUserRegionsQuery';
import { validateIncidentAddress } from 'app/pages/CreateEditWildfire/WildfireForm/WildfireForm.utils';
import { attemptAsync } from 'es-toolkit/util';
import { ConfirmCheckbox } from 'features/geoEvents/components/GeoEventChangelog/ConfirmCheckbox';
import {
  getGeoEventRegionsChanged,
  newRegionIsAdded,
} from 'features/geoEvents/hooks/useGeoEventRegionsChanged';
import { getGeoEventNotificationPreviewState } from 'features/geoEvents/utils/getGeoEventNotificationPreviewState';
import { PushNotificationPreview } from 'features/notifications/components/PushNotificationPreview';
import { provisionalEditIsDisabled } from 'features/geoEvents/geoEvent.utils';
import { TEXTAREA_MAX_ROWS } from '../../../../constants';
import { ReviewEditChanges } from './components/ReviewEditChanges';
import { EditAutomatedGeoEventFormMap } from './components/EditAutomatedGeoEventFormMap';
import { EditAutomatedGeoEventChangelog } from './components/EditAutomatedGeoEventChangelog';
import {
  FormValues,
  Status,
  buildEditedGeoEventData,
  buildProvisionalEditData,
  convertSubmittedDataToFormValues,
  getMatchingRegionsMemoized,
} from './EditAutomatedGeoEventForm.utils';

type EditAutomatedGeoEventFormProps = {
  geoEvent: Wildfire;
  reviewSubmission?: EditGeoEventProvisionalSubmissionResponse;
};

type UpdateGeoEventData = Wildfire &
  Pick<WildfireCreateUpdateData, 'reporterNotes'>;

const updateGeoEvent = (
  geoEvent: UpdateGeoEventData,
): Promise<AxiosResponse<Wildfire>> =>
  API.put(`geo_events/${geoEvent.id}`, geoEvent);

/** Module-level so the identity stays stable across renders. */
const identityRegionsChange = (
  newRegions: GeoEventRegion[],
): GeoEventRegion[] => newRegions;

export const EditAutomatedGeoEventForm = (
  props: EditAutomatedGeoEventFormProps,
): JSX.Element => {
  const { geoEvent, reviewSubmission } = props;
  const { t } = useTranslation();
  const history = useHistory();
  const queryClient = useQueryClient();
  const initialZoom = useInitialMapZoom();
  const isInternalTabEnabled = useReleaseFlag('internal_tab_on_incidents');

  const {
    lat,
    lng,
    isActive,
    reporterManaged,
    name,
    address,
    regions,
    data: { reporterOnlyNotes },
  } = geoEvent;

  const {
    regions: regionOptions,
    isLoading: isLoadingRegions,
    error: regionsError,
  } = useUserRegionsQuery();

  const validationSchema = useMemo(
    () =>
      Yup.object().shape(
        {
          status: Yup.string(),
          reporterManaged: Yup.boolean(),
          lat: Yup.number(),
          lng: Yup.number(),
          regions: Yup.array().when('reporterManaged', {
            is: true,
            then: Yup.array().min(
              1,
              t('editGeoEvent.inputs.counties.required'),
            ),
            /**
             * The field is disabled while bot-managed, so the only thing that
             * can add a county is the marker sync after a move.
             */
            otherwise: Yup.array().test(
              'countyChangeRequiresReporterManaged',
              t('editGeoEvent.inputs.counties.reporterManagedRequired'),
              (value) => !newRegionIsAdded(regions, value ?? []),
            ),
          }),
          address: Yup.string().when('reporterManaged', {
            is: true,
            then: Yup.string().required(
              t('createEditWildfire.inputs.address.required'),
            ),
            otherwise: Yup.string(),
          }),
          confirmAdditionalRegions: Yup.boolean().when(
            'confirmAdditionalRegions',
            {
              is: (exist: boolean) => typeof exist === 'boolean',
              then: Yup.boolean().oneOf([true]),
              otherwise: Yup.boolean(),
            },
          ),
          confirmRemovedRegions: Yup.boolean().when('confirmRemovedRegions', {
            is: (exist: boolean) => typeof exist === 'boolean',
            then: Yup.boolean().oneOf([true]),
            otherwise: Yup.boolean(),
          }),
          ...reporterNotesValidationSchema,
        },
        [
          ['confirmAdditionalRegions', 'confirmAdditionalRegions'],
          ['confirmRemovedRegions', 'confirmRemovedRegions'],
        ],
      ),
    [t, regions],
  );

  const initialValues = useMemo(() => {
    if (reviewSubmission) {
      return convertSubmittedDataToFormValues(
        reviewSubmission.submittedData,
        geoEvent,
      );
    }
    return {
      status: isActive ? 'active' : ('inactive' as Status),
      reporterManaged,
      reporterOnlyNotes: reporterOnlyNotes || '',
      address: address || '',
      regions,
      lat,
      lng,
      name,
      ...reporterNotesDefaultFormValues,
    };
  }, [
    reviewSubmission,
    geoEvent,
    reporterOnlyNotes,
    isActive,
    reporterManaged,
    address,
    regions,
    lat,
    lng,
    name,
  ]);

  const formMethods = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: initialValues,
    values: initialValues,
    mode: 'onBlur',
  });

  const {
    control,
    formState: { isDirty, errors, isSubmitting, isValid },
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
  } = formMethods;

  const [isGeocoding, setIsGeocoding] = useState(false);
  // Set aside when a reviewer turns Reporter Managed off, restored on re-check.
  const [stashedValues, setStashedValues] = useState<Pick<
    FormValues,
    'lat' | 'lng' | 'address' | 'regions'
  > | null>(null);

  const { data: permissions } = usePermissions({
    geoEventId: geoEvent.id,
    geoEventType: geoEvent.geoEventType,
  });
  const isProvisional =
    !reviewSubmission &&
    permissions?.wildfire?.geoEvent.editPermission === 'provisional';

  const updateGeoEventMutation = useMutation({
    mutationFn: (data: UpdateGeoEventData) => updateGeoEvent(data),
  });

  const navigateAfterSubmit = (): void => {
    // If the user got here from a link in Slack, there won't be any history to
    // go back to.
    if (history.length === 1) {
      history.push('/');
    } else {
      history.goBack();
    }
  };

  const { submitForReview, approveSubmission, rejectSubmission, isRejecting } =
    useProvisionalGeoEventActions({
      reviewSubmission,
      geoEvent,
      navigate: (path) => history.push(path),
      onSubmitForReviewSuccess: navigateAfterSubmit,
    });

  const handleGeoEventUpdate: SubmitHandler<FormValues> = async (
    values: FormValues,
  ) => {
    if (reviewSubmission) {
      await approveSubmission(buildProvisionalEditData(geoEvent, values));
      return;
    }

    if (isProvisional) {
      await submitForReview(buildProvisionalEditData(geoEvent, values));
      return;
    }

    const [error] = await attemptAsync(() =>
      updateGeoEventMutation.mutateAsync({
        ...geoEvent,
        isActive: values.status === 'active',
        reporterManaged: values.reporterManaged,
        address: values.address,
        regions: values.regions,
        lat: values.lat,
        lng: values.lng,
        data: buildEditedGeoEventData(geoEvent, values),
        reporterNotes: buildReporterNotesPayload(values),
      }),
    );

    if (!error) {
      await invalidateGeoEventQueries(queryClient, geoEvent.id);
      enqueueSnackbar(t('editGeoEvent.edited'), { variant: 'success' });
      navigateAfterSubmit();
      return;
    }

    if (
      isAxiosError(error) &&
      error.response?.status === HttpStatusCode.Forbidden
    ) {
      enqueueSnackbar(t('editGeoEvent.permissionError'), { variant: 'error' });
      return;
    }

    logUnexpectedErrorAndNotifyUserWithTryAgain(t, { error });
  };

  const reporterManagedValue = watch('reporterManaged');
  const addressValue = watch('address');
  const currentRegions = watch('regions');
  const latValue = watch('lat');
  const lngValue = watch('lng');

  const { regionsAdded, regionsRemoved } = useMemo(
    () => getGeoEventRegionsChanged(regions, currentRegions),
    [regions, currentRegions],
  );

  const previewBody = addressValue ?? '';

  const { showPreviewSection, showPreview, showNoNotificationNote } =
    getGeoEventNotificationPreviewState({
      geoEvent,
      currentNotificationType: geoEvent.notificationType,
      regionsAdded,
      title: name,
      body: previewBody,
      // Only reporter-managed edits can notify, so there is nothing to say
      // about notifications while the incident is bot-managed.
      isDirty: reporterManagedValue && isDirty,
      sendsNotifications: reporterManagedValue,
    });

  const isGeolocationLocked = !reporterManagedValue;

  /**
   * A bot-managed edit never notifies, so the "confirm added/removed regions"
   * checkboxes have nothing to confirm and must not block the submit. Matters
   * because a marker move syncs `regions` even while bot-managed.
   */
  const notifiesRegionChanges = reporterManagedValue;

  /**
   * Only lat/lng: the staged lookup already synced `regions`, and the address
   * stays bot-managed on this form.
   *
   * `shouldValidate` matters: the submit button is gated on `formState.isValid`,
   * and confirm checkboxes clear their value on unmount without revalidating,
   * so a move has to refresh validity or the button can stay dead with nothing
   * on screen to explain why.
   */
  const handleMarkerCommit = useCallback(
    (location: MarkerEditLocationArg) => {
      if (!location) return;
      // The reviewer moved the marker, so drop the pending restore.
      setStashedValues(null);
      setValue('lat', location.lat, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('lng', location.lng, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [setValue],
  );

  const markerEditRegionSync = useMarkerEditRegionSync({
    geoEventType: geoEvent.geoEventType,
    regionOptions,
    enabled: true,
    // No evac-zone bookkeeping on this form, so a county swap is unconditional.
    handleRegionsChange: identityRegionsChange,
    getValues,
    setValue,
  });

  const {
    onEnterEditMode: onMarkerEnterEditMode,
    onStage: onMarkerRegionStage,
    onCancel: onMarkerRegionCancel,
    getMarkerRegion,
  } = markerEditRegionSync;

  const markerLock = useMarkerEditLock({
    onCommit: handleMarkerCommit,
    isEditing: true,
    onEnterEditMode: onMarkerEnterEditMode,
    onStage: onMarkerRegionStage,
    onCancel: onMarkerRegionCancel,
  });

  const { checkbox: markerMoveCheckbox, isBlockingSubmit: markerMoveBlocking } =
    useMarkerMoveConfirmation({
      originalLatLng: { lat: geoEvent.lat, lng: geoEvent.lng },
      currentLatLng: { lat: latValue, lng: lngValue },
      isEditing: true,
    });

  const addressWarning = errors.address
    ? undefined
    : validateIncidentAddress(addressValue ?? '', t);

  const handleReporterManagedChange = async (
    checked: boolean,
  ): Promise<void> => {
    setValue('reporterManaged', checked, {
      shouldValidate: true,
      shouldDirty: true,
    });

    const locationUntouched =
      getValues('lat') === initialValues.lat &&
      getValues('lng') === initialValues.lng;

    /**
     * Turning off Reporter Managed on a reporter-managed submission sets its
     * location, address, and counties aside until it's turned back on. Skipped
     * once the reviewer has moved the marker.
     */
    const stashesValues =
      Boolean(reviewSubmission) &&
      !checked &&
      initialValues.reporterManaged &&
      locationUntouched;

    // The added county gets dropped, and the submitted move may be in it. The
    // sync hook can't tell, so move the marker back too.
    const revertsSubmittedLocation =
      stashesValues && newRegionIsAdded(regions, initialValues.regions);

    // Rewriting `regions` would lose a staged move's county, so cancel the move.
    const overwritesRegions =
      stashesValues || (checked && Boolean(stashedValues));
    if (overwritesRegions && !markerLock.isLocked) {
      markerLock.cancel();
    }

    if (stashesValues) {
      setStashedValues({
        lat: getValues('lat'),
        lng: getValues('lng'),
        address: getValues('address'),
        regions: getValues('regions'),
      });
      if (revertsSubmittedLocation) {
        setValue('lat', lat, { shouldDirty: true, shouldValidate: true });
        setValue('lng', lng, { shouldDirty: true, shouldValidate: true });
      }
      setValue('address', address || '', { shouldValidate: true });
      setValue('regions', regions, { shouldValidate: true });
      return;
    }

    if (!checked) {
      /**
       * The marker survives the toggle, since a move is reporter-owned and
       * bot-locks the coordinates on save.
       *
       * `regions` is the incident's counties plus the county under the marker
       * while bot-managed, so rebuild exactly that. Counties added by hand are
       * dropped: the field is disabled while bot-managed, so keeping one would
       * be an unreviewable county change, unless the marker sits in it. The
       * marker's county comes from the sync hook to keep this synchronous, as
       * a fresh geocode left the field empty whenever the lookup was dropped.
       */
      const markerRegion = getMarkerRegion();
      const botManagedRegions =
        markerRegion && !regions.some((region) => region.id === markerRegion.id)
          ? [...regions, markerRegion]
          : regions;

      setValue('address', address || '', { shouldValidate: true });
      setValue('regions', botManagedRegions, { shouldValidate: true });
      return;
    }

    if (stashedValues) {
      const options = { shouldDirty: true, shouldValidate: true };
      setValue('lat', stashedValues.lat, options);
      setValue('lng', stashedValues.lng, options);
      setValue('address', stashedValues.address, options);
      setValue('regions', stashedValues.regions, options);
    }
    setStashedValues(null);

    // Clear a county error the reporter just resolved by taking over.
    trigger('regions');

    // Skip the geocode if the incident already has an address.
    if (getValues('address').trim()) {
      return;
    }

    // Seed Address from a reverse-geocode so the reporter has a starting point
    // to refine rather than typing from scratch. Use the form's coordinates:
    // the marker may already have been moved away from the incident's.
    setIsGeocoding(true);
    const [, result] = await attemptAsync(() =>
      getMatchingRegionsMemoized(
        getValues('lat'),
        getValues('lng'),
        regionOptions,
        geoEvent.geoEventType,
      ),
    );
    // Guard against stale writes: only apply if the reporter hasn't toggled
    // back to bot-managed while the request was in flight.
    if (result?.placeName && getValues('reporterManaged')) {
      setValue('address', result.placeName, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
    setIsGeocoding(false);
  };

  if (isLoadingRegions) {
    return <Loader disableBackground />;
  }

  if (regionsError) {
    return <InlineStatus status="error" fullWidth />;
  }

  const formFields = (
    <Grid container spacing={2} sx={{ width: '100%', minHeight: '100%' }}>
      <Grid size={12}>
        <Typography variant="h3">{name}</Typography>
      </Grid>

      {reviewSubmission && (
        <Grid size={12}>
          <ReviewEditChanges
            geoEvent={geoEvent}
            reviewSubmission={reviewSubmission}
          />
        </Grid>
      )}

      {isProvisional && (
        <Grid size={12}>
          <Alert severity="info">
            {t('createEditWildfire.provisionalBanner')}
          </Alert>
        </Grid>
      )}

      <Grid size={6}>
        <Controller
          name="status"
          control={control}
          render={({ field, fieldState }) => {
            const { ref, ...muiFieldProps } = field;
            return (
              <TextField
                select
                id="geoevent-status"
                label={t('editGeoEvent.status')}
                fullWidth
                {...muiFieldProps}
                size="small"
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                required
                disabled={isSubmitting}
              >
                <MenuItem value="active">{t('editGeoEvent.active')}</MenuItem>
                <MenuItem value="inactive">
                  {t('editGeoEvent.inactive')}
                </MenuItem>
              </TextField>
            );
          }}
        />
      </Grid>

      <Grid size={6} sx={{ display: 'flex', alignItems: 'center' }}>
        <Controller
          name="reporterManaged"
          control={control}
          render={({ field }) => {
            return (
              <FormControlLabel
                sx={{ ml: 0, gap: 1 }}
                control={
                  <Switch
                    checked={field.value}
                    onChange={(event) => {
                      handleReporterManagedChange(event.target.checked);
                    }}
                  />
                }
                label={t('editGeoEvent.reporterManaged')}
              />
            );
          }}
        />
      </Grid>

      {stashedValues &&
        (stashedValues.lat !== latValue || stashedValues.lng !== lngValue) && (
          <Grid size={12}>
            <Alert severity="info">
              {t('editGeoEvent.proposedLocationReverted')}
            </Alert>
          </Grid>
        )}

      <Grid size={12}>
        <Controller
          name="regions"
          control={control}
          render={({ field, fieldState }): JSX.Element => {
            return (
              <FormControl fullWidth>
                <AutocompleteWithBackdrop
                  multiple
                  id="regions"
                  groupBy={(region) => region.state}
                  options={regionOptions}
                  getOptionLabel={(region) =>
                    `${region.displayName}, ${region.state}`
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  value={field.value}
                  onChange={(_e, newRegions) => {
                    field.onChange(newRegions);
                  }}
                  onBlur={field.onBlur}
                  disabled={isSubmitting || isGeolocationLocked}
                  renderOption={(
                    optionProps,
                    region,
                    { selected },
                  ): JSX.Element => {
                    const { key, ...otherProps } = optionProps;
                    return (
                      <li key={key} {...otherProps}>
                        <Checkbox
                          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                          checkedIcon={<CheckBoxIcon fontSize="small" />}
                          sx={{ marginRight: 1 }}
                          checked={selected}
                        />
                        {region.displayName}
                      </li>
                    );
                  }}
                  renderInput={(params): JSX.Element => (
                    <TextField
                      {...params}
                      name={field.name}
                      inputRef={field.ref}
                      variant="outlined"
                      label={t('editGeoEvent.inputs.counties.label')}
                      error={Boolean(fieldState.error)}
                      helperText={
                        fieldState.error?.message ||
                        (isGeolocationLocked &&
                          t('editGeoEvent.inputs.botManagedHint'))
                      }
                      required={!isGeolocationLocked}
                    />
                  )}
                />
              </FormControl>
            );
          }}
        />
      </Grid>

      <Grid size={12}>
        <Controller
          name="address"
          control={control}
          render={({ field, fieldState }): JSX.Element => {
            const { ref, ...muiFieldProps } = field;
            return (
              <TextField
                id="geoevent-address"
                label={t('createEditWildfire.inputs.address.label')}
                fullWidth
                {...muiFieldProps}
                inputRef={ref}
                error={Boolean(fieldState.error)}
                helperText={
                  fieldState.error?.message ||
                  addressWarning ||
                  (isGeolocationLocked
                    ? t('editGeoEvent.inputs.botManagedHint')
                    : t('createEditWildfire.inputs.address.helperText'))
                }
                required={!isGeolocationLocked}
                disabled={isSubmitting || isGeolocationLocked || isGeocoding}
                multiline
                minRows={1}
                maxRows={TEXTAREA_MAX_ROWS}
                color={addressWarning ? 'warning' : undefined}
                focused={Boolean(addressWarning)}
                slotProps={{
                  input: {
                    endAdornment: isGeocoding ? (
                      <CircularProgress size={20} />
                    ) : undefined,
                  },
                  formHelperText: {
                    sx: {
                      color: addressWarning ? 'warning.main' : undefined,
                    },
                  },
                }}
              />
            );
          }}
        />
      </Grid>

      {!isInternalTabEnabled && (
        <>
          <Grid size={12}>
            <Box sx={{ marginTop: 1 }}>
              <Typography variant="h3">
                {t('createEditWildfire.sections.internal')}
              </Typography>
            </Box>
          </Grid>

          <Grid size={12}>
            <InternalNotesInput
              initialValue={initialValues.reporterOnlyNotes}
            />
          </Grid>
        </>
      )}

      {showPreviewSection && (
        <Grid size={12}>
          <Box sx={{ marginTop: 1 }}>
            <Typography variant="h3">
              {t('createEditWildfire.sections.preview')}
            </Typography>
          </Box>
        </Grid>
      )}

      {showPreview && (
        <Grid size={{ xs: 12, sm: 6 }}>
          <PushNotificationPreview title={name} body={previewBody} />
        </Grid>
      )}

      {showNoNotificationNote && (
        <Grid size={12}>
          <Typography
            sx={{
              fontWeight: 'medium',
              textAlign: 'center',
              marginTop: 1,
            }}
          >
            {t('createEditWildfire.noPushNotification')}
          </Typography>
        </Grid>
      )}

      {!reviewSubmission && (
        <EditAutomatedGeoEventChangelog geoEvent={geoEvent} />
      )}

      {markerMoveCheckbox && <Grid size={12}>{markerMoveCheckbox}</Grid>}

      {notifiesRegionChanges && regionsAdded && (
        <Grid size={12}>
          <ConfirmCheckbox
            fieldName="confirmAdditionalRegions"
            translationKey="geoEventChangelog.confirmAddedRegions"
          />
        </Grid>
      )}

      {notifiesRegionChanges && regionsRemoved && (
        <Grid size={12}>
          <ConfirmCheckbox
            fieldName="confirmRemovedRegions"
            translationKey="geoEventChangelog.confirmRemovedRegions"
          />
        </Grid>
      )}

      {reviewSubmission ? (
        <Grid size={12} container spacing={2}>
          <Grid size={6}>
            <Button
              type="submit"
              fullWidth
              size="large"
              color="success"
              variant="contained"
              disabled={
                !isValid ||
                isSubmitting ||
                isRejecting ||
                markerLock.hasStagedChange ||
                markerMoveBlocking
              }
            >
              {isSubmitting ? (
                <CircularProgress size={24} />
              ) : (
                t('reviewSubmission.buttons.approve')
              )}
            </Button>
          </Grid>
          <Grid size={6}>
            <Button
              fullWidth
              size="large"
              color="error"
              variant="contained"
              onClick={rejectSubmission}
              disabled={isSubmitting || isRejecting}
            >
              {isRejecting ? (
                <CircularProgress size={24} />
              ) : (
                t('reviewSubmission.buttons.reject')
              )}
            </Button>
          </Grid>
        </Grid>
      ) : (
        <Grid size={12}>
          <Button
            fullWidth
            type="submit"
            size="large"
            disabled={
              !isValid ||
              isSubmitting ||
              markerLock.hasStagedChange ||
              markerMoveBlocking ||
              provisionalEditIsDisabled({
                isProvisional,
                isEditing: true,
                isDirty,
                currentValues: getValues(),
                initialValues,
              })
            }
          >
            {t(
              isProvisional
                ? 'createEditWildfire.buttons.createProvisional'
                : 'common.submit',
            )}
          </Button>
        </Grid>
      )}
    </Grid>
  );

  return (
    <FormProvider {...formMethods}>
      <MapFormLayout>
        <MapFormLayout.MapContent>
          <EditAutomatedGeoEventFormMap
            geoEvent={geoEvent}
            markerLock={markerLock}
            zoom={initialZoom}
            center={
              reviewSubmission
                ? { lat: initialValues.lat, lng: initialValues.lng }
                : undefined
            }
          />
        </MapFormLayout.MapContent>
        <MapFormLayout.FormContent disablePadding={isInternalTabEnabled}>
          <FormWithoutEnter
            noValidate
            onSubmit={handleSubmit(handleGeoEventUpdate)}
          >
            {isInternalTabEnabled ? (
              <GeoEventFormTabs<FormValues>
                idPrefix="automated-geo-event-form"
                internalFieldNames={['newReporterNote']}
                internalContent={
                  <InternalFormSection
                    key={getIncidentRemountKey('internal-form', geoEvent)}
                    geoEventId={geoEvent.id}
                    newReporterNoteInitialValue={initialValues.newReporterNote}
                  />
                }
              >
                {formFields}
              </GeoEventFormTabs>
            ) : (
              formFields
            )}
          </FormWithoutEnter>
        </MapFormLayout.FormContent>
      </MapFormLayout>
    </FormProvider>
  );
};
